import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText, setSession, type SessionUser } from '../lib/api';
import { Button, Card, ErrorText, Field, Input } from '../components/ui';

type LoginResp = { step: 'TOTP'; challenge: string } | { step: 'ENROL'; challenge: string; otpauthUrl: string; secret: string; qrDataUrl: string };
type SessionResp = { accessToken: string; user: SessionUser; recoveryCodes?: string[] };

export function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<LoginResp | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [pending, setPending] = useState<SessionResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setStep(await api<LoginResp>('/auth/login', { body: { email, password } }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!step) return;
    setBusy(true);
    setError(null);
    try {
      const s = await api<SessionResp>(step.step === 'ENROL' ? '/auth/2fa/enrol' : '/auth/2fa/verify', { body: { challenge: step.challenge, code: code.trim() } });
      if (s.recoveryCodes) {
        setCodes(s.recoveryCodes);
        setPending(s);
      } else {
        setSession(s.accessToken, s.user);
        nav('/');
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="flex w-full max-w-[420px] flex-col gap-5 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#2b4a78] bg-accent-bg font-mono text-sm font-semibold text-accent">VTF</div>
          <div className="flex flex-col">
            <h1 className="m-0 text-lg font-bold">Virtual Trading Floor</h1>
            <span className="text-sm text-sub">Private desk · members only</span>
          </div>
        </div>

        {codes && pending ? (
          <div className="flex flex-col gap-3">
            <h2 className="m-0 text-base font-semibold">Save your recovery codes</h2>
            <p className="m-0 text-sm text-sub">Each code works once if you lose your phone. They won't be shown again.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-field p-3 font-mono text-sm">
              {codes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <Button
              variant="primary"
              onClick={() => {
                setSession(pending.accessToken, pending.user);
                nav('/');
              }}
            >
              I've saved them, continue
            </Button>
          </div>
        ) : !step ? (
          <form onSubmit={submitPassword} className="flex flex-col gap-4">
            <Field label="Email">
              <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password">
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <ErrorText error={error} />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Signing in…' : 'Continue'}
            </Button>
            <a href="/forgot-password" className="self-center text-sm">
              Forgot password?
            </a>
            <span className="text-center text-sm text-sub">
              Not a member yet? <a href="/apply">Apply to join the floor</a>
            </span>
          </form>
        ) : (
          <form onSubmit={submitCode} className="flex flex-col gap-4">
            {step.step === 'ENROL' ? (
              <>
                <p className="m-0 text-sm text-sub">Two-step sign-in is required. Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code.</p>
                <img src={step.qrDataUrl} alt="QR code for your authenticator app" className="mx-auto h-44 w-44 rounded-lg bg-white p-2" />
                <span className="text-center font-mono text-xs break-all text-muted">{step.secret}</span>
              </>
            ) : (
              <p className="m-0 text-sm text-sub">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
            )}
            <Field label="Code">
              <Input inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} mono />
            </Field>
            <ErrorText error={error} />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Checking…' : step.step === 'ENROL' ? 'Turn on and sign in' : 'Sign in'}
            </Button>
            <button type="button" className="cursor-pointer text-sm text-sub" onClick={() => setStep(null)}>
              Start again
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
